<template>
  <div class="confirm-booking-page">
    <div class="page-header">
      <h1 class="page-title">{{ $t('booking.confirmBooking') }}</h1>
    </div>

    <!-- Main Card -->
    <el-card class="main-card" shadow="never">
      <!-- Related Information -->
      <div class="section-divider">
        <div class="section-header">
          <el-icon class="section-icon"><UserFilled /></el-icon>
          <h3 class="section-title">{{ $t('booking.confirm.relatedInfo') }}</h3>
        </div>
        <div class="info-content">
          <div class="info-row">
            <span class="info-label">
              <el-icon class="label-icon"><User /></el-icon>
              {{ $t('booking.teacher') }}
            </span>
            <span class="info-value">{{ teacherName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">
              <el-icon class="label-icon"><Avatar /></el-icon>
              {{ $t('booking.customer') }}
            </span>
            <span class="info-value">{{ customerName }}</span>
          </div>
        </div>
      </div>

      <!-- Date & Time -->
      <div class="section-divider">
        <div class="section-header">
          <el-icon class="section-icon"><Calendar /></el-icon>
          <h3 class="section-title">{{ $t('booking.confirm.dateTime') }}</h3>
        </div>
        <div class="info-content">
          <div class="info-row">
            <span class="info-label">{{ $t('booking.selectDate') }}</span>
            <span class="info-value">{{ bookingForm.date }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('booking.selectHours') }}</span>
            <div class="hours-tags">
              <el-tag 
                v-for="hour in selectedHours" 
                :key="hour" 
                type="primary"
                class="hour-tag"
              >
                {{ hour }}
              </el-tag>
            </div>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('booking.confirm.totalHours') }}</span>
            <span class="info-value highlight">{{ selectedHours.length }} {{ $t('booking.confirm.hours') }}</span>
          </div>
        </div>
      </div>

      <!-- VIP Information -->
      <div class="section-divider" v-if="hasVip">
        <div class="section-header">
          <el-icon class="section-icon"><Trophy /></el-icon>
          <h3 class="section-title">{{ $t('booking.confirm.vipInfo') }}</h3>
        </div>
        <div class="info-content">
          <div class="info-row">
            <span class="info-label">{{ $t('booking.vipPackage') }}</span>
            <span class="info-value highlight">{{ vipPackageName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('booking.remainingHours') }}</span>
            <span class="info-value highlight" :class="{ 'warning': remainingHours < 5 }">
              {{ remainingHours }} / {{ totalHours }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('booking.confirm.hours') }}</span>
            <span class="info-value">{{ selectedHours.length }}</span>
          </div>
        </div>
      </div>

      <!-- Booking Policies -->
      <div class="section-divider policies-section">
        <div class="section-header">
          <el-icon class="section-icon"><InfoFilled /></el-icon>
          <h3 class="section-title">{{ $t('booking.policies.title') }}</h3>
        </div>
        <el-tabs v-model="activePolicyTab" class="policy-tabs">
          <el-tab-pane v-if="policies.cancellationPolicy" :label="$t('booking.policies.cancellation')" name="cancellation">
            <div class="policy-content" v-html="policies.cancellationPolicy"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.safetyRules" :label="$t('booking.policies.safety')" name="safety">
            <div class="policy-content" v-html="policies.safetyRules"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.riderRequirements" :label="$t('booking.policies.riderRequirements')" name="rider">
            <div class="policy-content" v-html="policies.riderRequirements"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.arrivalInstructions" :label="$t('booking.policies.arrival')" name="arrival">
            <div class="policy-content" v-html="policies.arrivalInstructions"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.whatToBring" :label="$t('booking.policies.whatToBring')" name="bring">
            <div class="policy-content" v-html="policies.whatToBring"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.healthDisclosure" :label="$t('booking.policies.health')" name="health">
            <div class="policy-content" v-html="policies.healthDisclosure"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.weatherPolicy" :label="$t('booking.policies.weather')" name="weather">
            <div class="policy-content" v-html="policies.weatherPolicy"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.lateArrivalPolicy" :label="$t('booking.policies.lateArrival')" name="late">
            <div class="policy-content" v-html="policies.lateArrivalPolicy"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.photoReleasePolicy" :label="$t('booking.policies.photoRelease')" name="photo">
            <div class="policy-content" v-html="policies.photoReleasePolicy"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.termsAndConditions" :label="$t('booking.policies.terms')" name="terms">
            <div class="policy-content" v-html="policies.termsAndConditions"></div>
          </el-tab-pane>
          <el-tab-pane v-if="policies.liabilityWaiver" :label="$t('booking.policies.liability')" name="liability">
            <div class="policy-content" v-html="policies.liabilityWaiver"></div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <el-button class="edit-btn" @click="handleEdit">
          <el-icon><ArrowLeft /></el-icon>
          {{ $t('booking.confirm.edit') }}
        </el-button>
        <el-button
          type="primary"
          class="submit-btn"
          :loading="submitting"
          @click="handleSubmit"
        >
          <el-icon><Check /></el-icon>
          {{ $t('booking.confirm.submit') }}
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { UserFilled, Calendar, Check, ArrowLeft, Trophy, InfoFilled, User, Avatar } from '@element-plus/icons-vue'
import { useBookingStore } from '@/stores/booking'
import { getBookingPolicies } from '@/api/booking'
import MTime from '@/utils/time-handler'

const router = useRouter()
const { t } = useI18n()
const bookingStore = useBookingStore()

const submitting = ref(false)
const activePolicyTab = ref('cancellation')
const policies = reactive({
  cancellationPolicy: '',
  safetyRules: '',
  riderRequirements: '',
  arrivalInstructions: '',
  whatToBring: '',
  healthDisclosure: '',
  weatherPolicy: '',
  lateArrivalPolicy: '',
  photoReleasePolicy: '',
  termsAndConditions: '',
  liabilityWaiver: ''
})

// Get data from store
const bookingData = computed(() => bookingStore.pendingBookingData)

const hasVip = computed(() => bookingData.value?.hasVip || false)
const vipPackageName = computed(() => bookingData.value?.vipPackageName || '')
const remainingHours = computed(() => bookingData.value?.remainingHours || 0)
const totalHours = computed(() => bookingData.value?.totalHours || 0)

const bookingForm = reactive({
  teacherId: '',
  customerId: '',
  date: '',
  hours: []
})

const selectedHours = ref([])

// Computed properties - use names directly from store
const teacherName = computed(() => {
  return bookingData.value?.teacherName || '-'
})

const customerName = computed(() => {
  return bookingData.value?.customerName || '-'
})

// Load booking policies
const loadPolicies = async () => {
  try {
    const response = await getBookingPolicies()
    if (response.success && response.data) {
      const data = response.data
      policies.cancellationPolicy = data.cancellationPolicy || ''
      policies.safetyRules = data.safetyRules || ''
      policies.riderRequirements = data.riderRequirements || ''
      policies.arrivalInstructions = data.arrivalInstructions || ''
      policies.whatToBring = data.whatToBring || ''
      policies.healthDisclosure = data.healthDisclosure || ''
      policies.weatherPolicy = data.weatherPolicy || ''
      policies.lateArrivalPolicy = data.lateArrivalPolicy || ''
      policies.photoReleasePolicy = data.photoReleasePolicy || ''
      policies.termsAndConditions = data.termsAndConditions || ''
      policies.liabilityWaiver = data.liabilityWaiver || ''
    }
  } catch (error) {
    console.error('Failed to load booking policies:', error)
  }
}

// Load data from store
const loadFromStore = () => {
  if (!bookingData.value) {
    ElMessage.warning(t('booking.confirm.invalidData'))
    router.push('/booking/add')
    return
  }
  
  const data = bookingData.value
  bookingForm.teacherId = data.teacherId || ''
  bookingForm.customerId = data.customerId || ''
  bookingForm.date = data.date || ''
  selectedHours.value = data.hours || []
  bookingForm.hours = selectedHours.value
  
  // Validate required data
  if (!bookingForm.teacherId || !bookingForm.customerId || !bookingForm.date || selectedHours.value.length === 0) {
    ElMessage.warning(t('booking.confirm.invalidData'))
    router.push('/booking/add')
  }
}

// Submit booking
const handleSubmit = async () => {
  if (submitting.value) return
  
  submitting.value = true
  
  try {
    // Prepare booking data - API expects bookingDays as array of objects
    // Use new server-side date format: simple UTC midnight from local date components
    const dayOfWeek = new Date(bookingForm.date).getDay() + 1  // JavaScript getDay() returns 0-6, we need 1-7
    const bookingDateUTC = MTime.formatBookingDate(bookingForm.date)
    
    const bookingData = {
      customerId: bookingForm.customerId,
      teacherId: bookingForm.teacherId,
      useVipHours: hasVip.value, // Use VIP hours if customer has VIP
      bookingDays: [
        {
          bookingDate: bookingDateUTC,
          bookingDayNumber: dayOfWeek,
          hours: selectedHours.value.map(hour => parseInt(hour.split(':')[0]))
        }
      ]
    }
    
    console.log('[Booking Submit] Sending data:', bookingData)
    
    // Create booking
    const result = await bookingStore.addBooking(bookingData)
    
    console.log('[Booking Submit] Store returned:', result)
    console.log('[Booking Submit] result.success =', result.success)
    
    // ALWAYS show result in console to debug
    if (!result.success) {
      console.error('[Booking Submit] FAILED - Should show error dialog')
    } else {
      console.log('[Booking Submit] SUCCESS - Should show success dialog')
    }
    
    if (result.success) {
      // Show success message with OK button
      await ElMessageBox.alert(
        t('booking.addSuccess'),
        'Booking Confirmed',
        {
          confirmButtonText: 'OK',
          type: 'success',
          callback: () => {
            router.push('/booking')
          }
        }
      )
    } else {
      // Show error message with OK button
      await ElMessageBox.alert(
        result.message || result.error || t('booking.addFailed'),
        'Booking Failed',
        {
          confirmButtonText: 'OK',
          type: 'error'
        }
      )
    }
  } catch (error) {
    console.error('[Booking Submit] Exception:', error)
    // Show error message with OK button
    await ElMessageBox.alert(
      error.message || t('booking.addFailed'),
      'Booking Failed',
      {
        confirmButtonText: 'OK',
        type: 'error'
      }
    )
  } finally {
    submitting.value = false
  }
}

// Go back to edit
const handleEdit = () => {
  // Pass current booking data via query parameters to preserve selections
  const data = bookingData.value
  const query = {
    teacherId: data.teacherId,
    customerId: data.customerId,
    date: data.date,
    hours: (data.hours || []).join(','),
    hasVip: String(data.hasVip || false),
    vipPackageName: data.vipPackageName || '',
    remainingHours: String(data.remainingHours || 0),
    totalHours: String(data.totalHours || 0)
  }
  router.push({ path: '/booking/add', query })
}

onMounted(() => {
  loadFromStore()
  loadPolicies()
})
</script>

<style scoped lang="scss">
.confirm-booking-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 16px;
  
  .page-header {
    margin-bottom: 20px;
    
    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #1f2329;
      text-align: center;
    }
  }
  
  .main-card {
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    overflow: hidden;
    
    :deep(.el-card__body) {
      padding: 0;
    }
    
    .section-divider {
      padding: 16px 20px;
      
      &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
      }
      
      .section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        
        .section-icon {
          font-size: 18px;
          color: #409eff;
        }
        
        .section-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          color: #1f2329;
        }
      }
      
      .info-content {
        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding: 8px 0;
          border-bottom: 1px solid #f5f7fa;
          
          &:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: none;
          }
          
          .info-label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            color: #606266;
            
            .label-icon {
              font-size: 16px;
              color: #409eff;
            }
          }
          
          .info-value {
            font-size: 13px;
            color: #1f2329;
            font-weight: 500;
            
            &.highlight {
              color: #409eff;
              font-weight: 600;
            }
            
            &.warning {
              color: #f56c6c;
            }
          }
          
          .hours-tags {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
            
            .hour-tag {
              background: #409eff;
              border-color: #409eff;
              color: #fff;
              padding: 4px 10px;
              border-radius: 12px;
              font-size: 12px;
            }
          }
        }
      }
    }
    
    .policies-section {
      .policy-tabs {
        margin-top: 8px;
        
        :deep(.el-tabs__header) {
          margin-bottom: 16px;
        }
        
        :deep(.el-tabs__item) {
          font-size: 14px;
          padding: 0 16px;
          height: 40px;
          line-height: 40px;
        }
        
        :deep(.el-tabs__content) {
          min-height: 120px;
        }
        
        .policy-content {
          font-size: 14px;
          line-height: 1.8;
          color: #606266;
          padding: 12px 0;
          
          p {
            margin: 0 0 12px 0;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
          
          ul, ol {
            margin: 8px 0;
            padding-left: 24px;
          }
          
          li {
            margin-bottom: 6px;
          }
          
          strong {
            color: #303133;
            font-weight: 600;
          }
        }
      }
    }
  }
  
  .form-actions {
    margin-top: 20px;
    padding: 16px 20px;
    background: #fafbfc;
    border-top: 1px solid #f0f0f0;
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: flex-end;
    
    .submit-btn {
      min-width: 140px;
      height: 44px;
      font-size: 15px;
      font-weight: 500;
      background: linear-gradient(90deg, #409eff 0%, #66b1ff 100%);
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
      
      &:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-2px);
      }
      
      &:disabled {
        background: #dcdfe6;
        box-shadow: none;
        cursor: not-allowed;
      }
      
      .el-icon {
        margin-right: 8px;
      }
    }
    
    .edit-btn {
      min-width: 110px;
      height: 44px;
      font-size: 15px;
      background: #fff;
      border: 1px solid #dcdfe6;
      border-radius: 8px;
      color: #606266;
      
      &:hover {
        border-color: #c0c4cc;
        color: #909399;
      }
      
      .el-icon {
        margin-right: 8px;
      }
    }
  }
}

// Responsive design
@media (max-width: 480px) {
  .confirm-booking-page {
    padding: 16px 12px;
    
    .main-card {
      .section-divider {
        padding: 16px;
        
        .section-header {
          .section-icon {
            font-size: 18px;
          }
          
          .section-title {
            font-size: 15px;
          }
        }
        
        .info-content {
          .info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            
            .info-label {
              font-size: 14px;
            }
            
            .info-value {
              font-size: 14px;
            }
          }
        }
      }
    }
    
    .form-actions {
      padding: 16px;
      gap: 8px;
      
      .submit-btn,
      .edit-btn {
        min-width: 100px;
        height: 44px;
        font-size: 15px;
      }
    }
  }
}
</style>
